/**
 * Chat API Service
 * Uses the shared apiClient from api.ts (includes token refresh interceptor)
 */

import { apiClient } from './api';
import type { ApiResponse } from '@/types/api';
import type { ChatMessageState, ConversationSummary } from '@/types/chat';
import type { ExpenseCategory } from '@/lib/bills-display';
import { normalizeRawMessage, type RawMessage } from './chatHelpers';

// ─── SSE Nonce ───────────────────────────────────────────────────

/**
 * POST /api/auth/sse-token
 * Fetches a short-lived one-time nonce to use instead of the full JWT when
 * opening an SSE connection. The nonce is valid for 30 seconds and is
 * consumed on first use by the backend, preventing replay.
 */
export async function getSSENonce(): Promise<string> {
  const response = await apiClient.post<ApiResponse<{ token: string }>>('/auth/sse-token');
  const nonce = response.data?.data?.token;
  if (!nonce) throw new Error('Failed to obtain SSE nonce');
  return nonce;
}

// ─── API Methods ─────────────────────────────────────────────────

export const chatApi = {
  /**
   * GET /api/chat/conversations
   * Returns user's conversation list
   */
  getConversations: async (params?: {
    status?: string;
    limit?: number;
  }): Promise<ConversationSummary[]> => {
    const response = await apiClient.get<
      ApiResponse<{ conversations: ConversationSummary[] }>
    >('/chat/conversations', { params });
    return response.data.data?.conversations ?? [];
  },

  /**
   * GET /api/chat/history/:conversationId
   * Returns messages for a conversation
   */
  getHistory: async (
    conversationId: string,
    limit = 50
  ): Promise<ChatMessageState[]> => {
    const response = await apiClient.get<
      ApiResponse<{ conversation_id: string; messages: RawMessage[] }>
    >(`/chat/history/${conversationId}`, { params: { limit } });

    const messages = response.data.data?.messages ?? [];
    return messages.map(normalizeRawMessage);
  },

  /**
   * POST /api/chat/stream/:conversationId/message
   * Send a message (response arrives via SSE)
   */
  sendStreamingMessage: async (
    conversationId: string,
    message: string,
    page_context?: { family_id: string; family_name: string } | null,
  ): Promise<void> => {
    const body: Record<string, unknown> = { message };
    if (page_context) body.page_context = page_context;
    const response = await apiClient.post(
      `/chat/stream/${conversationId}/message`,
      body
    );
    if (response.data.status !== 'success') {
      throw new Error(
        response.data.error || response.data.message || 'Couldn’t send your message. Please try again.',
      );
    }
  },

  /**
   * PATCH /api/chat/conversations/:id/title
   * Rename a conversation
   */
  renameConversation: async (conversationId: string, title: string): Promise<void> => {
    await apiClient.patch(`/chat/conversations/${conversationId}/title`, { title });
  },

  /**
   * POST /api/chat/conversations/:id/end
   * #1731 phase 6. Idempotent. `closingTurn` is null while
   * CHAT_CLOSING_TURN_ENABLED is unset, which is the state on production today.
   */
  endConversation: async (conversationId: string): Promise<{ closingTurn: string | null }> => {
    const response = await apiClient.post<ApiResponse<{ closingTurn: string | null }>>(
      `/chat/conversations/${conversationId}/end`
    );
    return { closingTurn: response.data.data?.closingTurn ?? null };
  },

  /**
   * POST /api/chat/conversations/:id/resume
   * #1731 phase 6. Idempotent. Throws on 404, which is also the contract for a
   * voice thread — view-only in the text viewer.
   */
  resumeConversation: async (conversationId: string): Promise<void> => {
    await apiClient.post(`/chat/conversations/${conversationId}/resume`);
  },

  /**
   * POST /api/chat/conversations/:id/resume-after-crisis (#2566)
   * The family continues a conversation a critical Guardian flag paused.
   * Idempotent: a conversation that is not paused keeps its mode.
   */
  resumeAfterCrisis: async (conversationId: string): Promise<void> => {
    await apiClient.post(`/chat/conversations/${conversationId}/resume-after-crisis`);
  },

  /**
   * DELETE /api/chat/conversations/:id
   * Permanently delete a conversation and all its messages
   */
  deleteConversation: async (conversationId: string): Promise<void> => {
    await apiClient.delete(`/chat/conversations/${conversationId}`);
  },

  /**
   * POST /api/tasks
   * Create a task (called when user confirms a create_task action card)
   */
  createTask: async (data: Record<string, string>): Promise<{ id: string | null }> => {
    const response = await apiClient.post('/tasks', data);
    if (response.data.status !== 'success') {
      throw new Error(response.data.error || 'Couldn’t create the task. Please try again.');
    }
    return { id: response.data?.data?._id ?? response.data?.data?.id ?? null };
  },

  /**
   * POST /api/documents/upload
   * Submit a receipt for reimbursement (#641 — replaces the retired
   * medical-bill upload). The backend creates the ReimbursementClaim
   * synchronously; OCR enrichment is fire-and-forget backfill, so there
   * is nothing to poll.
   */
  uploadReceipt: async (
    file: File,
    category: ExpenseCategory,
    amount: number
  ): Promise<{ claim_id: string }> => {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('kind', 'receipt');
    formData.append('category', category);
    formData.append('amount', String(amount));
    const response = await apiClient.post<ApiResponse<{ claim?: { _id?: string } }>>(
      '/documents/upload',
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const claimId = response.data.data?.claim?._id;
    if (!claimId) throw new Error('Upload failed: no claim returned');
    return { claim_id: String(claimId) };
  },

  /**
   * POST /api/chat/suggestions
   * Haiku-generated suggestion pills for the home Maya panel.
   * Sends current family context; backend returns 3 labels with routing kinds.
   */
  getMayaSuggestions: async (ctx: {
    time_of_day: 'morning' | 'afternoon' | 'evening' | 'day';
    companion_name: string;
    parent_first_name: string;
    child_first_name: string;
    open_tasks: number;
    helpers: number;
    has_next_appt: boolean;
    next_appt_in_days: number | null;
    has_active_meds: boolean;
  }): Promise<{ label: string; kind: string }[]> => {
    const response = await apiClient.post<
      ApiResponse<{ suggestions: { label: string; kind: string }[] }>
    >('/chat/suggestions', ctx);
    return response.data.data?.suggestions ?? [];
  },
};

export default chatApi;
